import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle2,
  Loader2,
  AlertCircle,
  Zap,
  BookOpen,
  Users,
  Compass,
  MessageSquare,
  Send,
  Calendar,
} from 'lucide-react';
import api from '../services/api';
import { BOOK_CALL_URL } from '../constants/links';

// ---------------------------------------------------------------------------
// Static content
// ---------------------------------------------------------------------------
const AEC_SERVICES = [
  {
    id: 'implementation',
    icon: Zap,
    title: 'Tool Implementation',
    description: 'End-to-end setup, configuration, and integration of AEC software into your existing workflows.',
  },
  {
    id: 'audit',
    icon: BookOpen,
    title: 'Technology Audit',
    description: 'Review your current tech stack to identify gaps, redundancies, and upgrade opportunities.',
  },
  {
    id: 'training',
    icon: Users,
    title: 'Team Training',
    description: 'Structured onboarding and upskilling programmes tailored to your team\'s experience level.',
  },
  {
    id: 'strategy',
    icon: Compass,
    title: 'Digital Strategy',
    description: 'Long-term technology roadmapping aligned with your business goals and project pipeline.',
  },
];

const SERVICE_OPTIONS = [
  'Automation / Workflows',
  'CRM / ERP Setup',
  'Cloud / Infrastructure',
  'Website / App',
  'Data & Analytics',
  'Integrations with existing systems',
  'IT Advisory Only',
];

const TECH_STACK_OPTIONS = [
  'Google Workspace',
  'Microsoft 365',
  'AWS / Azure',
  'CRM (Salesforce, HubSpot, Zoho)',
  'Accounting (QuickBooks, Xero)',
];

const TIMELINE_OPTIONS = [
  'As soon as possible',
  '1–2 weeks',
  '1–2 months',
  'Not urgent / flexible',
];

const BUDGET_OPTIONS = [
  '< $500',
  '< $5,000',
  '< $10,000',
  'Above $10,000',
  'Not sure',
];

const inputClass = 'w-full px-3 py-2.5 text-sm rounded-xl bg-gray-100 border border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white text-gray-900 placeholder-gray-400 transition-all';

// ---------------------------------------------------------------------------
// Main page — the site's single contact destination.
// ---------------------------------------------------------------------------
const AECConsulting = () => {
  const [searchParams] = useSearchParams();
  const sourceTool = searchParams.get('tool') ?? '';
  const arrivingForAec = sourceTool || searchParams.get('mode') === 'aec';
  const teamHero = searchParams.get('hero') === 'team';

  const [submitState, setSubmitState] = useState('idle'); // idle | submitting | success | error
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    website: '',
    message: '',
    services: [],
    servicesOther: '',
    techStack: [],
    techStackOther: '',
    timeline: '',
    budget: '',
    aecInquiry: Boolean(arrivingForAec),
    serviceInterest: sourceTool ? ['implementation'] : [],
  });

  const updateField = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setError('');
  };

  const toggleCheckbox = (field, value) => {
    setForm((p) => ({
      ...p,
      [field]: p[field].includes(value)
        ? p[field].filter((v) => v !== value)
        : [...p[field], value],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) return;
    if (form.aecInquiry && form.serviceInterest.length === 0) return;

    setSubmitState('submitting');
    setError('');

    const services = [...form.services];
    if (form.servicesOther.trim()) services.push(form.servicesOther.trim());
    const techStack = [...form.techStack];
    if (form.techStackOther.trim()) techStack.push(form.techStackOther.trim());
    const serviceInterestLabels = form.serviceInterest
      .map((id) => AEC_SERVICES.find((s) => s.id === id)?.title || id);

    try {
      await api.post('/send-email', {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        company: form.company,
        website: form.website,
        description: form.message,
        services,
        techStack,
        timeline: form.timeline,
        budget: form.budget,
        aecInquiry: form.aecInquiry,
        serviceInterest: form.aecInquiry ? serviceInterestLabels : [],
        sourceToolSlug: sourceTool || '',
      });
      setSubmitState('success');
    } catch {
      setError('Failed to send your request. Please try again or email us directly at contact@stray-apps.com');
      setSubmitState('error');
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-20">

        {/* ── Hero ── */}
        <div className="pt-14 pb-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 mb-5">
            <MessageSquare className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-3">
            {teamHero ? 'See it built for your team' : "Let's Build Something Together"}
          </h1>
          <p className="text-base text-gray-500 leading-relaxed max-w-xl mx-auto mb-6">
            {teamHero
              ? 'Happy to share a couple of ideas specific to your setup — worth a quick call?'
              : "Tell us what you need — custom software, workflow automation, or specialised AEC technology consulting — and we'll get back to you within one business day."}
          </p>
          {sourceTool && (
            <p className="text-sm text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-4 py-1.5 inline-block mb-6">
              Enquiring about help with <span className="font-semibold">{sourceTool}</span>
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={scrollToForm}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold text-sm transition-all shadow-lg"
            >
              Get in touch
            </button>
            <a
              href={BOOK_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-purple-200 text-purple-700 hover:border-purple-400 hover:bg-purple-50 font-semibold text-sm transition-all"
            >
              <Calendar className="w-4 h-4" /> Book a Call
            </a>
          </div>
        </div>

        {/* ── Contact form ── */}
        <section id="contact-form" className="scroll-mt-20 bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
          {submitState === 'success' ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-500" />
              <p className="text-lg font-semibold text-gray-900">Thanks, {form.firstName}!</p>
              <p className="text-sm text-gray-500 max-w-sm">
                We've received your request and will get back to you within 24 hours.
              </p>
              <a
                href={BOOK_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
              >
                <Calendar className="w-4 h-4" /> Book a Call
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-1">Work Request</h2>
                <p className="text-sm text-gray-400">We'll get back to you within one business day.</p>
              </div>

              {/* Identity fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">
                    First Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="John"
                    value={form.firstName}
                    onChange={(e) => updateField('firstName', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">
                    Last Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Doe"
                    value={form.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="john@company.com"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Company</label>
                  <input
                    type="text"
                    placeholder="Acme Corp"
                    value={form.company}
                    onChange={(e) => updateField('company', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 font-medium mb-1 block">Website</label>
                <input
                  type="url"
                  placeholder="https://example.com"
                  value={form.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  className={inputClass}
                />
              </div>

              {/* AEC Consulting opt-in */}
              <div className="border border-gray-200 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.aecInquiry}
                    onChange={(e) => updateField('aecInquiry', e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">This is an AEC consulting inquiry</span>
                    <br />
                    Architecture, Engineering & Construction — tool implementation, tech audits, training, or digital strategy.
                  </span>
                </label>

                {form.aecInquiry && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {AEC_SERVICES.map(({ id, icon: Icon, title, description }) => {
                      const selected = form.serviceInterest.includes(id);
                      return (
                        <label
                          key={id}
                          className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                            selected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleCheckbox('serviceInterest', id)}
                            className="sr-only"
                          />
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${selected ? 'bg-purple-600' : 'bg-purple-50'}`}>
                            <Icon className={`w-5 h-5 ${selected ? 'text-white' : 'text-purple-600'}`} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{title}</p>
                            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Services */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Services Needed</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SERVICE_OPTIONS.map((opt) => (
                    <label key={opt} className="flex items-center gap-2 p-2 rounded-lg hover:bg-purple-50 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={form.services.includes(opt)}
                        onChange={() => toggleCheckbox('services', opt)}
                        className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">{opt}</span>
                    </label>
                  ))}
                </div>
                <input
                  type="text"
                  value={form.servicesOther}
                  onChange={(e) => updateField('servicesOther', e.target.value)}
                  placeholder="Other (please specify)"
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                />
              </div>

              {/* Tech stack */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Current Technical Stack</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {TECH_STACK_OPTIONS.map((opt) => (
                    <label key={opt} className="flex items-center gap-2 p-2 rounded-lg hover:bg-purple-50 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={form.techStack.includes(opt)}
                        onChange={() => toggleCheckbox('techStack', opt)}
                        className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">{opt}</span>
                    </label>
                  ))}
                </div>
                <input
                  type="text"
                  value={form.techStackOther}
                  onChange={(e) => updateField('techStackOther', e.target.value)}
                  placeholder="Other (please specify)"
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                />
              </div>

              {/* Timeline */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Expected Delivery Timeline</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TIMELINE_OPTIONS.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center justify-center p-3 rounded-lg border text-sm cursor-pointer transition-all text-center ${
                        form.timeline === opt
                          ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                          : 'border-gray-200 hover:border-purple-300 text-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="timeline"
                        value={opt}
                        checked={form.timeline === opt}
                        onChange={() => updateField('timeline', opt)}
                        className="sr-only"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Budget Range</label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {BUDGET_OPTIONS.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center justify-center p-3 rounded-lg border text-sm cursor-pointer transition-all text-center ${
                        form.budget === opt
                          ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                          : 'border-gray-200 hover:border-purple-300 text-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="budget"
                        value={opt}
                        checked={form.budget === opt}
                        onChange={() => updateField('budget', opt)}
                        className="sr-only"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="text-xs text-gray-500 font-medium mb-1 block">Project Description</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => updateField('message', e.target.value)}
                  placeholder="Please explain briefly about your requirements..."
                  className={`${inputClass} resize-none`}
                />
              </div>

              {error && (
                <p className="text-sm text-red-600 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </p>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitState === 'submitting'}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg disabled:opacity-50"
                >
                  {submitState === 'submitting' ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Submit Request</>
                  )}
                </button>
              </div>
            </form>
          )}
        </section>

      </div>
    </div>
  );
};

export default AECConsulting;
